// Libraries
import { CloseIcon } from '@chakra-ui/icons';
import { Flex, IconButton, Text } from '@chakra-ui/react';
import { Fragment } from 'react';
// Components
import { InformationFooter } from '../InformationFooter';
import { InformationList } from '../InformationList';
// Utils
import text from '@translations/en.json';
// Types
import { InformationModalProps } from './InformationModal.types';

/**
 * InformationModal component represents a modal for displaying information.
 *
 * @component
 * @param {InformationModalProps} props - The InformationModalProps component props.
 * @param {Function} props.onContinue - Callback function invoked when the user continues.
 * @param {string[]} props.errors - An array of error messages.
 * @param {string[]} props.warnings - An array of warning messages.
 * @returns {ReactElement} The InformationModal component.
 */
export const InformationModal = ({
  onContinue,
  onCancel = () => { return },
  errors = [],
  warnings = [],
  title = '',
  footer = '',
  hideDefaultFooter = false,
  hideDefaultTitle = false,
  showCancelButton = false,
}: InformationModalProps) => {
  const errorsTitle = errors.length ? ` ${text.errors}` : '';
  const warningsTitle = warnings.length ? ` ${text.warnings}` : '';
  const titleConnector = errors.length && warnings.length ? ` &` : '';
  const informTitleText = `${errorsTitle}${titleConnector}${warningsTitle}:`;
  const informFooterText = errors.length ? text.schedule_modal_error_footer : text.Schedule_modal_warn_footer;

  return (
    <div id='modal-container'>
      <Fragment>
        <IconButton
          position='absolute'
          top={2.5}
          right={2.5}
          h={8}
          minW={8}
          aria-label={text.close_menu}
          variant='baseLight'
          icon={<CloseIcon color='text.faded' />}
          onClick={showCancelButton?onCancel:onContinue}
        />
        <Flex minH={8} align='center'>
          <Text fontSize='m' color='text.base' fontWeight='medium'>
            {`${hideDefaultTitle ? title : text.schedule_modal_variable_title + informTitleText}`}
          </Text>
        </Flex>
        <InformationList errors={errors} warnings={warnings} />
        <InformationFooter
          message={hideDefaultFooter ? footer : informFooterText}
          onClose={onContinue}
          onCancel={onCancel}
          showCancelButton={showCancelButton} />
      </Fragment>
    </div>
  );
};
